<template>
  <div>
    <DefaultCrudView
      @create-new="createNewSession"
      :breadcrumbsItems="breadcrumbs"
      :actions="crudActions"
      containerLarge
    >
      <template slot="content">
        <v-data-table
          :headers="headers"
          :items="sessions"
          class="elevation-1"
          :loading="loading"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:item.title="{ item }">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2">mdi-movie-open-check-outline</v-icon>
              {{ item.title || `${$t('aiwf_sb_session_session')} #${item.session_id}` }}
            </div>
          </template>

          <template v-slot:item.session_id="{ item }">
            {{ item.session_id }}
          </template>

          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                  @click.stop="selectSession(item)"
                >
                  mdi-open-in-new
                </v-icon>
              </template>
              <span>{{ $t('aiwf_sb_session_open_storyboard') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                  @click.stop="deleteSession(item)"
                  :loading="deletingSessionId === item.session_id"
                >
                  mdi-delete-outline
                </v-icon>
              </template>
              <span>{{ $t('delete') }}</span>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <div class="text-center pa-8">
              <v-icon size="64" color="grey lighten-1">mdi-movie-open-check-outline</v-icon>
              <p class="mt-4 text-h6 grey--text">{{ $t('aiwf_sb_session_no_sessions') }}</p>
              <p class="text-body-2 grey--text">{{ $t('aiwf_sb_session_no_sessions_description') }}</p>
            </div>
          </template>
        </v-data-table>

        <!-- Load More Button -->
        <div v-if="!loading && hasMoreSessions" class="text-center py-5">
          <v-btn @click="loadMoreSessions" block small text>
            <v-icon color="primary">mdi-refresh</v-icon>{{ $t('load_more') }}
          </v-btn>
        </div>
      </template>
    </DefaultCrudView>

    <!-- New Storyboard Form Component -->
    <NewStoryboardForm
      v-model="showStoryboardModal"
      @submit="handleCreateStoryboard"
      @close="closeStoryboardModal"
      ref="storyboardForm"
    />
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import DefaultCrudView from '@/components/DefaultCrudView.vue'
import NewStoryboardForm from '@/components/ai_workforce/storyboard/NewStoryboardForm.vue'
import StoryboardActions from '@/store/ai_workforce/chat/actions-types'
import ContentActions from '@/store/content/actions/actions-types'
import storyboardApi from '@/services/api/ai_workforce/chat'
import AppActions from '@/store/app/actions-types'

export default {
  name: 'SessionListView',

  components: {
    DefaultCrudView,
    NewStoryboardForm
  },

  data() {
    return {
      loading: false,
      creatingSession: false,
      deletingSessionId: null,
      sessions: [],
      allSessions: [],
      sessionsLimit: 10,
      sessionFirst: true,
      hasMoreSessions: false,
      options: {
        sortBy: ['created_at'],
        sortDesc: [true]
      },
      crudActions: [
        {
          text: "aiwf_sb_session_create_new_film",
          eventToEmit: "create-new",
        }
      ],
      // Modal data
      showStoryboardModal: false
    }
  },

  computed: {
    ...mapState('app', {
      selectedAiWorkforceApp: state => state.selectedAiWorkforceApp,
    }),

    ...mapGetters("app", {
      getUserApplicationById: "getUserApplicationById"
    }),

    headers() {
      return [
        {
          text: this.$t('aiwf_sb_session_name'),
          value: 'title',
          sortable: true
        },
        {
          text: this.$t('aiwf_sb_session_session_id'),
          value: 'session_id',
          sortable: false
        },
        {
          text: this.$t('aiwf_sb_session_date'),
          value: 'created_at',
          sortable: true
        },
        {
          text: this.$t('aiwf_sb_session_actions'),
          value: 'actions',
          sortable: false,
          align: 'center'
        }
      ]
    },

    breadcrumbs() {
      return [
        {
          text: this.$t('aiwf_sb_session_title'),
          prependIcon: 'mdi-movie-open',
        },
      ]
    },

    agentConfig() {
      const agent = this.getUserApplicationById(this.selectedAiWorkforceApp)

      return this.parseJson(agent.config)
    }
  },

  watch: {
    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },
  },

  async mounted() {
    await this.loadSessions()
  },

  methods: {
   ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),

    ...mapActions('ai_workforce/chat', [
      StoryboardActions.GET_USER_SESSIONS,
      StoryboardActions.REMOVE_USER_SESSION,
    ]),

    ...mapActions('content/actions', [
      ContentActions.UPLOAD_FILE,
    ]),

    async loadSessions() {
      this.loading = true
      try {
        if (this.sessionFirst) {
          this.sessionFirst = false

          // Initial load - fetch all sessions from API
          const response = await this[StoryboardActions.GET_USER_SESSIONS]({})
          this.allSessions = response || []

          // Sort all sessions by date (newest first)
          this.allSessions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        }

        // Simulate pagination by slicing the allSessions array
        this.sessions = this.allSessions.slice(0, this.sessionsLimit)

        // Update pagination state
        this.hasMoreSessions = this.sessionsLimit < this.allSessions.length

      } catch (error) {
        console.error('Error loading sessions:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('aiwf_sb_session_error_loading') + " " + error.message)
      } finally {
        this.loading = false
      }
    },

    async loadMoreSessions() {
      this.sessionsLimit += 10
      await this.loadSessions()
    },

    reloadData() {
      this.loading = true
      try {
        let sortedSessions = [...this.allSessions]

        // Apply sorting
        if (this.options.sortBy.length > 0) {
          const sortBy = this.options.sortBy[0]
          const sortDesc = this.options.sortDesc[0]

          sortedSessions.sort((a, b) => {
            let aVal = a[sortBy]
            let bVal = b[sortBy]

            // Handle title sorting (use session name or fallback)
            if (sortBy === 'title') {
              aVal = a.title || `Session #${a.session_id}`
              bVal = b.title || `Session #${b.session_id}`
            }

            // Handle date sorting
            if (sortBy === 'created_at') {
              aVal = new Date(aVal)
              bVal = new Date(bVal)
            }

            if (aVal < bVal) return sortDesc ? 1 : -1
            if (aVal > bVal) return sortDesc ? -1 : 1
            return 0
          })
        }

        // Simulate pagination by slicing the allSessions array
        this.sessions = sortedSessions.slice(0, this.sessionsLimit)

      } catch (error) {
        console.error('Error reloading data:', error)
      } finally {
        this.loading = false
      }
    },

    async createNewSession() {
      // Open the modal instead of navigating
      this.showStoryboardModal = true
    },

    closeStoryboardModal() {
      this.showStoryboardModal = false
    },

    async handleCreateStoryboard(data) {
      const { formData, formattedText } = data

      // Set loading state on the form component
      this.$refs.storyboardForm.setLoading(true)

      try {

        // Step 1: Call create-session endpoint
        const sessionResponse = await storyboardApi.createUserSession()

        if (!sessionResponse || !sessionResponse.data || !sessionResponse.data.session_id) {
          throw new Error('Invalid session response: missing session_id')
        }

        const sessionId = sessionResponse.data.session_id

        await storyboardApi.renameUserSession({session_id: sessionId, title: formData.filmTitle})

        if (!formattedText || formattedText.trim().length === 0) {
          throw new Error('Failed to create formatted text from form data')
        }

        // Step 3: Call send_message endpoint with session_id and text
        const messageData = {
          chatInput: formattedText,
          sessionId: sessionId,
          rootNodeId: this.agentConfig.root_node_id,
        }

        const messageResponse = await storyboardApi.sendMessage(messageData)

        if (!messageResponse || !messageResponse.data) {
          throw new Error('Invalid message response: missing data')
        }

        // Step 4: Extract JSON from the response text
        let storyboardJson = null

        try {
          // Get response text from various possible fields
          const responseText = messageResponse.data.text ||
                              messageResponse.data.message ||
                              messageResponse.data.content ||
                              messageResponse.data.response ||
                              messageResponse.data[0].output || ''

          if (!responseText) {
            throw new Error('No text content found in API response')
          }

          // Try multiple JSON extraction methods
          let jsonString = null

          // Method 1: Look for JSON block markers
          const jsonBlockMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/i)
          if (jsonBlockMatch) {
            jsonString = jsonBlockMatch[1].trim()
          }

          // Method 2: Look for curly braces content
          if (!jsonString) {
            const jsonMatch = responseText.match(/\{[\s\S]*\}/g)
            if (jsonMatch && jsonMatch.length > 0) {
              // Take the largest JSON object found
              jsonString = jsonMatch.reduce((a, b) => a.length > b.length ? a : b).trim()
            }
          }

          // Method 3: Try parsing the entire response as JSON
          if (!jsonString) {
            jsonString = responseText.trim()
          }

          if (!jsonString) {
            throw new Error('No JSON content found in response text')
          }

          // Parse the JSON
          storyboardJson = JSON.parse(jsonString)

          // Validate that we have a valid storyboard structure
          if (!storyboardJson || typeof storyboardJson !== 'object') {
            throw new Error('Parsed JSON is not a valid object')
          }

          if (formData.imageReference) {
            const parentId = storyboardJson.FOLDER_PARENT_ID

            await this[ContentActions.UPLOAD_FILE]({parentId, file: formData.imageReference})
              .then(async (response) => {
                const imgReferenceMessage = {
                  chatInput: `Eu quero que voce substitua o atibuto NODE_ID do objeto REFERENCIA_VISUAL do json do storyboard por "${response.nodeId}". Depois quero que você execute a ferramenta (Img2Txt). O resultado da ferramenta Img2Txt deve ser um texto em portugues que deve ir para o VALOR do objeto REFERENCIA_VISUAL do json do storyboard. Retorne para mim o json completo do storyboard.`,
                  sessionId: sessionId,
                  rootNodeId: this.agentConfig.root_node_id,
                }

                await storyboardApi.sendMessage(imgReferenceMessage)
                
                this[AppActions.OPEN_APP_SUCCESS_MESSAGE]('aiwf_sb_form_image_uploaded')
              })
              .catch((error) => {
                console.error('Error uploading image:', error)
                this[AppActions.OPEN_APP_ERROR_MESSAGE](error.message)
              })
          }
        } catch (parseError) {
          console.error('Error parsing JSON from response:', parseError)
          console.error('Raw response data:', messageResponse.data)

          throw new Error(`Failed to parse JSON from API response: ${parseError.message}`)
        }

        // Step 5: Close modal and navigate to StoryboardForm with the JSON data
        this.closeStoryboardModal()

        const navigationData = {
          name: 'storyboard-form',
          params: { appId: this.selectedAiWorkforceApp, contentAppId: this.agentConfig.content_app_id },
          query: {
            sessionId
          }
        }

        await this.$router.push(navigationData)

      } catch (error) {
        console.error('Error in storyboard creation process:', error)

        // Show detailed error message
        let errorMessage = 'An error occurred during storyboard creation'

        if (error.message) {
          errorMessage = error.message
        } else if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (error.response && error.response.statusText) {
          errorMessage = `API Error: ${error.response.statusText}`
        }

        this[AppActions.OPEN_APP_ERROR_MESSAGE](errorMessage)
      } finally {
        // Set loading state on the form component
        this.$refs.storyboardForm.setLoading(false)
      }
    },

    async selectSession(session) {
      const navigationData = {
        name: 'storyboard-form',
        params: { appId: this.selectedAiWorkforceApp, contentAppId: this.agentConfig.content_app_id },
        query: {
          sessionId: session.session_id,
        }
      }

      this.$router.push(navigationData)
    },

    async deleteSession(session) {
      if (!confirm(this.$t('aiwf_sb_session_confirm_delete'))) {
        return
      }

      this.deletingSessionId = session.session_id
      try {
        await this[StoryboardActions.REMOVE_USER_SESSION](session)
        // Remove from both displayed sessions and all sessions
        this.sessions = this.sessions.filter(s => s.session_id !== session.session_id)
        this.allSessions = this.allSessions.filter(s => s.session_id !== session.session_id)

        // Update hasMoreSessions state
        this.hasMoreSessions = this.sessionsLimit < this.allSessions.length

        this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('aiwf_sb_session_session_deleted'))
      } catch (error) {
        console.error('Error deleting session:', error)
        this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('aiwf_sb_session_error_deleting') + " " + error.message)
      } finally {
        this.deletingSessionId = null
      }
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    getStatusColor(status) {
      switch (status) {
        case 'active':
          return 'success'
        case 'inactive':
          return 'error'
        default:
          return 'primary'
      }
    },

    parseJson(str)
    {
      try {
        return JSON.parse(str)
      } catch (e) {
      return {}
      }
    },

    parseResultData (messageResponse) {
        const responseText = messageResponse.data.text ||
                            messageResponse.data.message ||
                            messageResponse.data.content ||
                            messageResponse.data.response ||
                            messageResponse.data.output ||
                            messageResponse.data[0]?.output || ''
        return responseText
    },

    parseResultToJson (responseText) {
        let storyboardJson = null
        try {

          if (!responseText) {
            throw new Error('No text content found in API response')
          }

          let jsonString = null

          // Method 1: Look for JSON block markers
          const jsonBlockMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/i)
          if (jsonBlockMatch) {
            jsonString = jsonBlockMatch[1].trim()
          }

          // Method 2: Look for curly braces content
          if (!jsonString) {
            const jsonMatch = responseText.match(/\{[\s\S]*\}/g)
            if (jsonMatch && jsonMatch.length > 0) {
              // Take the largest JSON object found
              jsonString = jsonMatch.reduce((a, b) => a.length > b.length ? a : b).trim()
            }
          }

          // Method 3: Try parsing the entire response as JSON
          if (!jsonString) {
            jsonString = responseText.trim()
          }

          if (!jsonString) {
            throw new Error(responseText)
          }

          // Parse the JSON
          try {
            storyboardJson = JSON.parse(jsonString)
          } catch (jsonError) {
            throw new Error(responseText)
          }

          // Validate that we have a valid storyboard structure
          if (!storyboardJson || typeof storyboardJson !== 'object') {
            throw new Error('Parsed JSON is not a valid object: ' + responseText)
          }

        } catch (parseError) {
          console.error('Error parsing JSON from response:', parseError)
          throw new Error(parseError.message)
        }

        return storyboardJson
    },
  },
}
</script>

<style scoped>
.session-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.session-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
